/* Loans, letters, expenses, assets. */
import { normIban } from "../../domain/compliance";
import { evLetterIssued, evRequestDecided } from "./events";
import { moneyIn } from "./mailer";
import { countryOf } from "../../domain/country";
import { isISODate } from "../../domain/dates";
import { wageOf } from "../../domain/payroll";
import type * as T from "../types";
import { E, fmtMoney, fmtNum, has, isInt, isNum, sanitizeName, str, type Ctx } from "./ctx";
import { putFile } from "./derived";
import { decide, selfOrBody } from "./leave";

export const LOAN_TYPES = ["Salary advance", "Personal loan"];
export const LETTER_KINDS = ["Salary certificate", "Experience letter", "No objection certificate", "Salary transfer letter", "Embassy letter"];

export function letterText(t: T.Tenant, e: T.Employee, l: T.Letter, today: string): string {
  let b = `${t.name} — ${t.nameAr}\nCR ${t.cr} · VAT ${t.vat} · ${t.city}\n\nDate: ${today}\nRef: LTR-${l.id}\n\nTo: ${l.to}\n\n${l.kind.toUpperCase()}\n\n`;
  switch (l.kind) {
    case "Salary certificate":
    case "Salary transfer letter":
      b += `This is to certify that ${e.nameEn} (${e.code}), ID ${e.idNumber}, has been employed with us as ${e.title} since ${e.joined}, with a monthly salary of ${countryOf(t.country).currency.code} ${fmtMoney(wageOf(e))} (basic ${fmtMoney(e.basic)}, housing ${fmtMoney(e.housing)}, transport ${fmtMoney(e.transport)}).\n`;
      if (l.kind === "Salary transfer letter") b += `We undertake to transfer the employee's salary to IBAN ${normIban(e.iban)} until further notice.\n`;
      break;
    case "Experience letter":
      b += `This is to certify that ${e.nameEn} (${e.code}) has worked with us as ${e.title} since ${e.joined}.\n`;
      break;
    case "No objection certificate":
      b += `We have no objection to ${e.nameEn} (${e.code}), ID ${e.idNumber}, ${e.title}, regarding the matter addressed to ${l.to}.\n`;
      break;
    default:
      b += `We confirm that ${e.nameEn} (${e.code}), ID ${e.idNumber}, is employed with us as ${e.title} since ${e.joined}, and we request that the necessary visa be granted.\n`;
  }
  return b + `\nThis letter is issued upon the employee's request without any liability on the company.\n\nHuman Resources\n${t.name}\n`;
}

export function requestsOps(c: Ctx) {
  const ownList = <X extends { id: number; employeeId: number }>(u: T.User, xs: X[]) => {
    const own = c.isEmployee(u) ? c.ownEid(u) : null;
    return xs.filter((x) => own == null || x.employeeId === own).sort((a, b) => b.id - a.id);
  };
  return {
    async listLoans(): Promise<T.Loan[]> { return ownList(c.principal("loans.list"), c.state.loans); },

    async createLoan(input: T.LoanInput): Promise<T.Loan> {
      const u = c.principal("loans.list");
      const empId = selfOrBody(c, u, input);
      const fe: Record<string, string> = {};
      if (!LOAN_TYPES.includes(str(input.type))) fe.type = "must be Salary advance or Personal loan";
      if (!isNum(input.amount) || input.amount <= 0) fe.amount = "must be > 0";
      if (!isInt(input.months) || input.months < 1 || input.months > 60) fe.months = "must be an integer between 1 and 60";
      if (Object.keys(fe).length) throw E.validation(fe);
      const e = c.emp(empId);
      const max = 3 * (e.basic + e.housing + e.transport);
      if (input.amount > max) throw E.field("amount", `max ${fmtMoney(max)} (3 months of basic + housing + transport)`);
      if (c.state.loans.some((l) => l.employeeId === empId && (l.status === "Pending" || l.status === "Repaying"))) throw E.conflict("employee already has an open loan");
      const id = c.nextId("loans", 701, () => c.state.loans.reduce((m, x) => Math.max(m, x.id), 0));
      const l: T.Loan = { id, employeeId: empId, type: input.type, amount: input.amount, months: input.months, paid: 0, status: "Pending", date: c.today };
      c.state.loans.push(l);
      c.audit(u, "Requested loan", "Loan", String(id), `${l.type} ${c.cur} ${fmtNum(l.amount)} / ${l.months} months for employee ${empId}`);
      return l;
    },

    async decideLoan(id: number, approve: boolean): Promise<T.Loan> {
      const u = c.principal("loans.decide");
      const l = decide(c.state.loans, id, approve, "loan", "Repaying");
      c.audit(u, approve ? "Approved loan" : "Rejected loan", "Loan", String(id), "");
      evRequestDecided(c, l.type, l.type === "Salary advance" ? "سلفة على الراتب" : "قرض شخصي", l.id, l.employeeId, `${moneyIn(l.amount, c.cur)} · ${l.months} months`, approve);
      return l;
    },

    async listLetters(): Promise<T.Letter[]> { return ownList(c.principal("letters.list"), c.state.letters); },

    async createLetter(input: T.LetterInput): Promise<T.Letter> {
      const u = c.principal("letters.list");
      const empId = selfOrBody(c, u, input);
      const fe: Record<string, string> = {};
      if (!LETTER_KINDS.includes(str(input.kind))) fe.kind = "must be one of " + LETTER_KINDS.join(", ");
      if (!str(input.to).trim()) fe.to = "required";
      if (Object.keys(fe).length) throw E.validation(fe);
      c.emp(empId);
      const id = c.nextId("letters", 901, () => c.state.letters.reduce((m, x) => Math.max(m, x.id), 0));
      const l: T.Letter = { id, employeeId: empId, kind: input.kind, to: input.to.trim(), status: "Pending", date: c.today };
      c.state.letters.push(l);
      c.audit(u, "Requested letter", "Letter", String(id), `${l.kind} for employee ${empId}`);
      return l;
    },

    async issueLetter(id: number): Promise<T.Letter> {
      const u = c.principal("letters.issue");
      const l = c.state.letters.find((x) => x.id === id);
      if (!l) throw E.notFound("letter not found");
      if (l.status === "Issued") throw E.conflict("letter already issued");
      const e = c.emp(l.employeeId);
      const ref = putFile(c, "letters", `${sanitizeName(l.kind)}-${l.id}.txt`, "text/plain", letterText(c.state.settings.tenant, e, l, c.today));
      l.status = "Issued";
      l.fileKey = ref.key;
      c.audit(u, "Issued letter", "Letter", String(id), ref.key);
      evLetterIssued(c, l);
      return l;
    },

    async listExpenses(): Promise<T.Expense[]> { return ownList(c.principal("expenses.list"), c.state.expenses); },

    async createExpense(input: T.ExpenseInput): Promise<T.Expense> {
      const u = c.principal("expenses.list");
      const empId = selfOrBody(c, u, input);
      const vendor = str(input.vendor), category = str(input.category), date = str(input.date), receipt = str(input.receiptKey);
      const fe: Record<string, string> = {};
      if (!vendor.trim()) fe.vendor = "required";
      if (!category.trim()) fe.category = "required";
      if (!isISODate(date)) fe.date = "valid date required";
      else if (date > c.today) fe.date = "cannot be in the future";
      const okA = isNum(input.amount);
      if (!okA || input.amount <= 0) fe.amount = "must be > 0";
      if (!isNum(input.vat) || input.vat < 0) fe.vat = "must be ≥ 0";
      else if (okA && input.vat > input.amount) fe.vat = "cannot exceed amount";
      if (receipt && !c.ownsKey(u, receipt)) fe.receiptKey = "invalid file key";
      if (Object.keys(fe).length) throw E.validation(fe);
      c.emp(empId);
      const id = c.nextId("expenses", 3001, () => c.state.expenses.reduce((m, x) => Math.max(m, x.id), 0));
      const x: T.Expense = { id, employeeId: empId, vendor: vendor.trim(), category: category.trim(), date, amount: input.amount, vat: input.vat, status: "Pending", ai: input.ai === true };
      if (receipt) x.receiptKey = receipt;
      c.state.expenses.push(x);
      c.audit(u, "Submitted expense", "Expense", String(id), `${x.vendor} ${c.cur} ${fmtNum(x.amount)}`);
      return x;
    },

    async decideExpense(id: number, approve: boolean): Promise<T.Expense> {
      const u = c.principal("expenses.decide");
      const x = decide(c.state.expenses, id, approve, "expense", "Approved");
      c.audit(u, approve ? "Approved expense" : "Rejected expense", "Expense", String(id), "");
      evRequestDecided(c, "Expense claim", "مطالبة مصروفات", x.id, x.employeeId, `${x.vendor} · ${moneyIn(x.amount, c.cur)}`, approve);
      return x;
    },

    async listAssets(): Promise<T.Asset[]> {
      c.principal("hr.modules");
      return c.state.assets.slice().sort((a, b) => (a.tag < b.tag ? -1 : a.tag > b.tag ? 1 : 0));
    },

    async createAsset(input: T.AssetInput): Promise<T.Asset> {
      const u = c.principal("hr.modules");
      const item = str(input.item).trim(), type = str(input.type).trim();
      const fe: Record<string, string> = {};
      if (!item) fe.item = "required";
      if (!type) fe.type = "required";
      const cost = has(input, "cost") ? input.cost : 0;
      if (!isNum(cost) || cost < 0) fe.cost = "must be ≥ 0";
      let empId: number | null = null;
      if (has(input, "employeeId")) { if (!isInt(input.employeeId)) fe.employeeId = "must be an integer"; else empId = input.employeeId; }
      if (Object.keys(fe).length) throw E.validation(fe);
      if (empId != null) c.emp(empId);
      const n = c.nextId("assets", 1, () => c.state.assets.reduce((m, a) => Math.max(m, Number(/(\d+)$/.exec(a.tag)?.[1] ?? 0)), 0));
      const a: T.Asset = { tag: "AST-" + String(n).padStart(4, "0"), item, type, employeeId: empId, serial: str(input.serial), cost, status: empId != null ? "Assigned" : "In stock", warranty: str(input.warranty) };
      c.state.assets.push(a);
      c.audit(u, "Added asset", "Asset", a.tag, a.item);
      return a;
    },
  };
}
